import slugify from 'slugify';
import { newSelectTranslation } from './selectTranslation';

const DEFAULT_SLUG = '-';

const LTI_PATH_SEGMENT = 'lti-view';
const COURSE_PATH_SEGMENT = 'course';
const LTI_COURSE_PATH = `/${LTI_PATH_SEGMENT}/${COURSE_PATH_SEGMENT}`;
const REGULAR_COURSE_PATH = `/${COURSE_PATH_SEGMENT}`;

const getLtiPath = (path: string): string => {
  if (!path || typeof path !== 'string') return path;
  if (path.includes(LTI_COURSE_PATH)) return path;
  return path.replace(REGULAR_COURSE_PATH, LTI_COURSE_PATH);
};

const isLtiPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return path.includes(LTI_COURSE_PATH);
};

const ensureLtiPath = (path: string, isLti?: boolean): string => {
  if (!path || typeof path !== 'string') return path;
  if (isLti || isLtiPath()) {
    return getLtiPath(path);
  }
  return path;
};

interface LocalizedName {
  name: { languageCode: string; value: string }[];
  shortId: string;
}

interface Course extends LocalizedName {
  exam?: {
    shortId: string;
    __typename?: string;
  };
}

type Category = LocalizedName;

interface Expert {
  firstName: string;
  lastName: string;
  shortId: string;
}

type Episode = LocalizedName;

type Playlist = LocalizedName;

export const getCategorySlug = (category: Category): string => {
  return (
    slugify(newSelectTranslation(category.name), {
      replacement: '-',
      lower: true,
      strict: true,
    }) || DEFAULT_SLUG
  );
};

export const getCategoryDetailUrl = (
  category: Category,
  locale: string
): string => {
  const slug = getCategorySlug(category);

  return `/${locale}/category/${category.shortId}/${slug}`;
};

export const getCourseSlug = (course: Course): string => {
  return (
    slugify(newSelectTranslation(course.name), {
      replacement: '-',
      lower: true,
      strict: true,
    }) || DEFAULT_SLUG
  );
};

export const getCourseDetailUrl = (
  course: Course,
  locale: string,
  isLti = false
): string => {
  const slug = getCourseSlug(course);
  const path = `/${locale}/course/${course.shortId}/${slug}`;
  return ensureLtiPath(path, isLti);
};

export const getExpertSlug = (expert: Expert): string => {
  return (
    slugify(`${expert.firstName} ${expert.lastName}`, {
      replacement: '-',
      lower: true,
      strict: true,
    }) || DEFAULT_SLUG
  );
};

export const getExpertDetailUrl = (expert: Expert, locale: string): string => {
  const slug = getExpertSlug(expert);
  return `/${locale}/expert/${expert.shortId}/${slug}`;
};

export const getEpisodeSlug = (episode: Episode): string => {
  return (
    slugify(newSelectTranslation(episode.name), {
      replacement: '-',
      lower: true,
      strict: true,
    }) || DEFAULT_SLUG
  );
};

export const getEpisodeDetailUrl = (
  course: Course,
  episode: Episode,
  locale: string,
  isLti = false
): string => {
  const courseSlug = getCourseSlug(course);
  const episodeSlug = getEpisodeSlug(episode);
  const path = `/${locale}/course/${course.shortId}/${courseSlug}/watch/${episode.shortId}/${episodeSlug}`;
  return ensureLtiPath(path, isLti);
};

export const getQuizPageUrl = (
  course: Course,
  quizShortId: string,
  locale: string,
  isLti = false
): string => {
  const courseSlug = getCourseSlug(course);
  const path = `/${locale}/course/${course.shortId}/${courseSlug}/quiz/${quizShortId}`;
  return ensureLtiPath(path, isLti);
};

export const getGamePageUrl = (
  course: Course,
  quizShortId: string,
  locale: string,
  isLti = false
): string => {
  const courseSlug = getCourseSlug(course);
  const path = `/${locale}/course/${course.shortId}/${courseSlug}/game/${quizShortId}`;
  return ensureLtiPath(path, isLti);
};

export const getTrailerPageUrl = (
  course: Course,
  locale: string,
  isLti = false
): string => {
  const courseSlug = getCourseSlug(course);
  const path = `/${locale}/course/${course.shortId}/${courseSlug}/trailer`;
  return ensureLtiPath(path, isLti);
};

export const getExamPageUrl = (
  course: Course,
  locale: string,
  isLti = false
): string => {
  const slug = getCourseSlug(course);
  const { shortId, __typename } = course.exam || {};

  const path = `/${locale}/course/${course.shortId}/${slug}/${__typename === 'Game' ? 'game' : 'quiz'}/${shortId}`;
  return ensureLtiPath(path, isLti);
};

export const getPlaylistSlug = (playlist: Playlist): string => {
  return (
    slugify(newSelectTranslation(playlist.name), {
      replacement: '-',
      lower: true,
      strict: true,
    }) || DEFAULT_SLUG
  );
};

export const getPlaylistDetailUrl = (
  playlist: Playlist,
  locale: string
): string => {
  const slug = getPlaylistSlug(playlist);

  return `/${locale}/playlist/${playlist.shortId}/${slug}`;
};

export const getPlaylistCourseDetailUrl = (
  playlist: Playlist,
  course: Course,
  locale: string
): string => {
  const playlistSlug = getPlaylistSlug(playlist);
  const courseSlug = getCourseSlug(course);

  return `/${locale}/playlist/${playlist.shortId}/${playlistSlug}/course/${course.shortId}/${courseSlug}`;
};

export const getPlaylistEpisodeDetailUrl = (
  playlist: Playlist,
  course: Course,
  episode: Episode,
  locale: string
): string => {
  const playlistSlug = getPlaylistSlug(playlist);
  const courseSlug = getCourseSlug(course);
  const episodeSlug = getEpisodeSlug(episode);

  return `/${locale}/playlist/${playlist.shortId}/${playlistSlug}/course/${course.shortId}/${courseSlug}/watch/${episode.shortId}/${episodeSlug}`;
};

export const getPlaylistCourseExamDetailUrl = (
  playlist: Playlist,
  course: Course,
  locale: string
): string => {
  const playlistSlug = getPlaylistSlug(playlist);
  const courseSlug = getCourseSlug(course);
  const { shortId, __typename: examType } = course.exam || {};

  return `/${locale}/playlist/${
    playlist.shortId
  }/${playlistSlug}/course/${course.shortId}/${courseSlug}/${examType === 'Game' ? 'game' : 'quiz'}/${shortId}`;
};
